import React, { useState, useEffect, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Row,
  Col,
  Form,
  InputGroup,
  FormControl,
  Button,
  Spinner,
  Modal,
  CardColumns,
  Card,
} from 'react-bootstrap';
import { ChevronCompactRight } from 'react-bootstrap-icons';
import Delete from 'components/Delete';
import { segmentPropType } from '../../props';
import EditDropdown from './EditDropdown';
import PitchDropdown from './PitchDropdown';

import '../../../LeadDetails/CustomerDetails/TilePreview.css';

import { DesignContext } from '@aerialytic/canvas-library';
import { LeadContext } from 'Leads/Details/hooks/context';
import { isDroneManagementConfigured } from 'drone';

const pitches = [
  '0',
  '5',
  '9',
  '14',
  '18',
  '23',
  '27',
  '30',
  '34',
  '37',
  '40',
  '43',
  '45',
  '47',
  '49',
  '51',
  '53',
];

function RoofSegmentsTab({
  designType,
  hasRGB,
  hasDSM,
  segments,
  resolution,
  roofAreasSqFt,
  roofAreasUnit,
  pitchInDegrees,
  onPitchInDegreesChange,
  segmentAzimuthHandler,
  segmentEaveHeightHandler,
  segmentPitchAndAreaHandler,
  deleteShapeHandler,
  addPointToPolygonHandler,
  removePointFromPolygonHandler,
  measureHandler,
  runOptimizationHandler,
  runEdgeDetectionHandler,
  runRectificationHandler,
  runAIHandler,
  canSelectDifferentImage,
  onDroneAppHandler,
}) {
  const {
    optimizer1IsRunning,
    optimizer2IsRunning,
    edgeDetectionIsRunning,
    measureIsRunning,
    AIIsRunning,
    rectificationIsRunning,
  } = useContext(DesignContext);

  const { lead, leadImagesAPI, thumbnailsAPI, newEndpoint, displaySnack } =
    useContext(LeadContext);

  const selectThumbnailEndpoint = newEndpoint('crm/lead/thumbnails/select/');

  const [serverSide, setServerSide] = useState(false);
  const degrees =
    pitchInDegrees !== undefined && pitchInDegrees !== null
      ? pitchInDegrees
      : true;
  const [depthForEdgeDetection, setDepthForEdgeDetection] = useState(true);

  const [selection, setSelection] = useState(false);
  const [loading, setLoading] = useState(true);
  const [leadImages, setLeadImages] = useState([]);
  const [selectedLeadImage, setSelectedLeadImage] = useState();
  const [leadImageIndices, setLeadImageIndices] = useState([]);

  const topRef = useRef(null);
  const bottomRef = useRef(null);

  const syncScroll = (source, target) => {
    if (target.current && source.current) {
      target.current.scrollLeft = source.current.scrollLeft;
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadLeadImages = async () => {
      setLoading(true);
      try {
        const data = await leadImagesAPI.list(`?lead=${lead.uid}`);
        const rgbImages = data.filter((v) => v.image_type === 'R');
        const leadThumbnails = (
          await Promise.all(
            rgbImages.map(async (item) => {
              try {
                const thumbnail = await thumbnailsAPI.send('POST', {
                  lead: lead.uid,
                  lead_image: item.id,
                });
                return { ...item, img_str: thumbnail };
              } catch (err) {
                console.log(err);
                return null;
              }
            })
          )
        ).filter(Boolean);
        if (cancelled) return;

        setLeadImages(leadThumbnails);
        setLeadImageIndices(leadThumbnails.map((_, index) => index));
        if (leadThumbnails.length > 0) {
          setSelectedLeadImage((current) => current || leadThumbnails[0].id);
        }
      } catch (err) {
        console.log(err);
        if (!cancelled) {
          displaySnack?.({
            variant: 'danger',
            message: 'Capture dates could not be loaded.',
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadLeadImages();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleEdit = (selectKey, segmentIdx) => {
    if (selectKey === 'add-point') {
      addPointToPolygonHandler(Number(segmentIdx));
    } else if (selectKey === 'remove-point') {
      removePointFromPolygonHandler(Number(segmentIdx));
    }
  };

  const handleThumbnailSelection = (e) => {
    const lead_image_id = selectedLeadImage;
    if (!lead_image_id) {
      displaySnack?.({
        variant: 'danger',
        message: 'No AI-ready capture date is available for this lead.',
      });
      setLoading(false);
      return;
    }
    setLoading(true);
    selectThumbnailEndpoint
      .send('POST', {
        lead: lead.uid,
        lead_image: lead_image_id,
      })
      .then((res) => {
        setTimeout(() => {
          runAIHandler(lead_image_id);
        }, 2000);
        setLoading(false);
        setSelection(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
        displaySnack?.({
          variant: 'danger',
          message: 'Selected capture date could not be prepared for AI.',
        });
      });
  };

  return (
    <>
      {selection && (
        <Modal
          show={selection}
          size="lg"
          onHide={() => setSelection(false)}
          aria-labelledby="contained-modal-title-vcenter"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Select a capture date</Modal.Title>
          </Modal.Header>
          {loading ? (
            <Modal.Body className="mx-3">
              <Spinner animation="border" size="lg" />
            </Modal.Body>
          ) : (
            <>
              <Modal.Body className="mx-3">
                <CardColumns>
                  {leadImages
                    .sort((a, b) => a.id - b.id)
                    .map((item) => {
                      return (
                        <Card
                          className={`bg-dark text-white card-hover vintage ${
                            item.id === selectedLeadImage
                              ? 'card-hover-selected'
                              : ''
                          }`}
                          onClick={() => setSelectedLeadImage(item.id)}
                        >
                          <Card.Img
                            className="img-thumbnail"
                            src={item.img_str}
                            alt="Card image"
                          />
                          <Card.ImgOverlay id={item.id}>
                            <small className="capture-date bg-dark">
                              {item.capture_date}
                            </small>
                          </Card.ImgOverlay>
                        </Card>
                      );
                    })}
                </CardColumns>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  className="ml-2"
                  onClick={() => handleThumbnailSelection()}
                >
                  Run AI
                </Button>
              </Modal.Footer>
            </>
          )}
        </Modal>
      )}
      {/* <Row className="mt-4">
        <Col>
          <Form.Check
            id="chk-measure-optimize-server-side"
            type="switch"
            checked={serverSide}
            onChange={(e) => setServerSide(e.target.checked)}
            label="Run Server-Side"
          />
        </Col>
      </Row> */}
      <Row className="mt-4">
        <Col sm="12">
          <Form.Check
            id="pitch"
            type="switch"
            checked={degrees}
            onChange={(e) => onPitchInDegreesChange(e.target.checked)}
            label={`Pitch in ${degrees ? 'Degrees' : 'Rise/Run'}`}
          />
        </Col>
      </Row>
      {/* <Row className="mt-4">
        <Col sm="12">
          <Form.Check
            id="depthForEdge"
            type="switch"
            checked={depthForEdgeDetection}
            onChange={(e) => setDepthForEdgeDetection(e.target.checked)}
            label="Use depth information in edge detection"
          />
        </Col>
      </Row> */}
      <Row className="mt-4">
        <Col>
          {canSelectDifferentImage && leadImages.length !== 0 && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setSelection(true)}
              className="m-1"
            >
              {AIIsRunning && (
                <Spinner animation="border" role="status" size="sm" />
              )}
              {AIIsRunning ? ' Running AI' : 'Select Different Image'}
            </Button>
          )}
          {isDroneManagementConfigured() && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={onDroneAppHandler}
              className="m-1"
            >
              Drone App
            </Button>
          )}
        </Col>
      </Row>
      <Row className="mt-1">
        <Col>
          <Button
            variant="primary"
            size="sm"
            className="m-1"
            onClick={() => runAIHandler()}
            disabled={!hasRGB || AIIsRunning || rectificationIsRunning}
            title={!hasRGB ? 'This canvas does not have RGB imagery.' : undefined}
          >
            {AIIsRunning && (
              <Spinner animation="border" role="status" size="sm" />
            )}
            {AIIsRunning ? ' Running AI' : ' Run AI'}
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            className="m-1"
            onClick={runRectificationHandler}
            disabled={AIIsRunning || rectificationIsRunning}
          >
            {rectificationIsRunning && (
              <Spinner animation="border" role="status" size="sm" />
            )}
            {' Rectify'}
          </Button>
        </Col>
      </Row>
      <Row className="mt-1 mb-4">
        <Col>
          <Button
            variant="outline-primary"
            size="sm"
            className="m-1"
            onClick={() => measureHandler(serverSide)}
            disabled={!hasDSM}
          >
            {measureIsRunning && (
              <Spinner animation="border" role="status" size="sm" />
            )}
            {' Measure'}     
          </Button>
          <ChevronCompactRight />
          <Button
            variant="outline-primary"
            size="sm"
            className="m-1"
            onClick={() => runOptimizationHandler(false, serverSide)}
          >
            {optimizer1IsRunning && (
              <Spinner animation="border" role="status" size="sm" />
            )}
            {' Optimize'}
          </Button>
          <ChevronCompactRight />
          <Button
            variant="outline-primary"
            size="sm"
            className="m-1"
            onClick={() =>
              runEdgeDetectionHandler(depthForEdgeDetection, serverSide)
            }
          >
            {edgeDetectionIsRunning && (
              <Spinner animation="border" role="status" size="sm" />
            )}
            {' Edge Detection'}
          </Button>
          {/* <Button size="sm" className="m-1" onClick={() => runOptimizationHandler(true)}>
            {'4-Optimize Vertex Position '}
            {optimizer2IsRunning && <Spinner animation="border" role="status" size="sm" />}
          </Button> */}
        </Col>
      </Row>
      <div
        className="mt-3"
        ref={topRef}
        style={{ overflowX: 'auto', overflowY: 'visible' }}
        onScroll={() => syncScroll(topRef, bottomRef)}
      >
        <div style={{ width: '620px', height: '1px' }} />
      </div>
      <div
        ref={bottomRef}
        style={{ overflowX: 'auto', overflowY: 'visible' }}
        onScroll={() => syncScroll(bottomRef, topRef)}
      >
        <div style={{ width: '620px', height: '1px' }} />
        <Row className="my-2 font-weight-bold flex-nowrap">
          <Col className="cell" style={{ minWidth: '60px' }}>
            #&nbsp;&nbsp;&nbsp;&nbsp;Row
          </Col>
          <Col className="cell pitch-cell" style={{ minWidth: '100px' }}>
            Pitch
          </Col>
          <Col className="cell">Azimuth</Col>
          <Col className="cell" style={{ minWidth: '100px' }}>
            Eave Height
          </Col>
          <Col className="cell" style={{ minWidth: '100px' }}>
            Area
          </Col>
          <Col className="cell" style={{ minWidth: '26px' }} />
        </Row>

        <Row className="flex-nowrap">
          <Col className="cell" style={{ minWidth: '60px' }} />
          <Col className="cell pitch-cell" style={{ minWidth: '100px' }}>
            <div className="font-size-sm">Set All:</div>
            <PitchDropdown
              degrees={degrees}
              pitches={pitches}
              segIdx={-1}
              segmentPitchAndAreaHandler={segmentPitchAndAreaHandler}
            />
          </Col>
          <Col className="cell" />
          <Col className="cell" style={{ minWidth: '100px' }}>
            <div className="font-size-sm">Set All:</div>
            <InputGroup size="sm">
              <Form.Control
                onChange={(e) => segmentEaveHeightHandler(-1, e.target.value)}
              />
              <InputGroup.Append>
                <InputGroup.Text>ft</InputGroup.Text>
              </InputGroup.Append>
            </InputGroup>
          </Col>
          <Col className="cell" style={{ minWidth: '100px' }} />
          <Col className="cell" style={{ minWidth: '26px' }} />
        </Row>

        {segments.map(({ id, pitch, azimuth, height }, index) => {
          const areaSqFt = Array.isArray(roofAreasSqFt)
            ? roofAreasSqFt[index]
            : null;
          const areaDisplay = Number.isFinite(areaSqFt) ? areaSqFt.toFixed(2) : '';
          const unit = roofAreasUnit || 'ft²';

          return (
            <Row key={id} className="flex-nowrap" style={{ height: 36 }}>
              <Col className="cell" style={{ minWidth: '60px' }}>
                #&nbsp;&nbsp;&nbsp;&nbsp;{index + 1}
              </Col>
              <Col className="cell pitch-cell" style={{ minWidth: '100px' }}>
                <PitchDropdown
                  degrees={degrees}
                  pitches={pitches}
                  segIdx={index}
                  segId={id}
                  pitch={pitch}
                  segmentPitchAndAreaHandler={segmentPitchAndAreaHandler}
                />
              </Col>
              <Col className="cell">
                <Form.Control
                  size="sm"
                  type="number"
                  placeholder="Azimuth"
                  value={azimuth}
                  onChange={(e) => segmentAzimuthHandler(index, e.target.value)}
                />
              </Col>
              <Col className="cell" style={{ minWidth: '100px' }}>
                <InputGroup size="sm">
                  <Form.Control
                    type="number"
                    step="0.01"
                    value={Number(height).toFixed(2)}
                    onChange={(e) =>
                      segmentEaveHeightHandler(index, e.target.value)
                    }
                  />
                  <InputGroup.Append>
                    <InputGroup.Text>ft</InputGroup.Text>
                  </InputGroup.Append>
                </InputGroup>
              </Col>
              <Col className="cell" style={{ minWidth: '100px' }}>
                <InputGroup size="sm">
                  <Form.Control
                    size="sm"
                    placeholder="Area"
                    value={areaDisplay}
                    readOnly
                  />
                  <InputGroup.Append>
                    <InputGroup.Text>{unit}</InputGroup.Text>
                  </InputGroup.Append>
                </InputGroup>
              </Col>
              <Col className="cell" style={{ minWidth: '26px' }}>
                <Delete id={id} onClick={() => deleteShapeHandler(id)} />
              </Col>
            </Row>
          );
        })}
      </div>
      <style>{`
        .cell {
          padding: 0rem 0.1em;
          white-space: nowrap;
          width: 90px;
          min-width: 90px;
          overflow: hidden;
        }
        .pitch-cell {
          overflow: visible;
          position: relative;
          z-index: 1;
        }
        .pitch-cell .dropdown-menu {
          z-index: 9999;
        }
      `}</style>
    </>
  );
}

RoofSegmentsTab.propTypes = {
  designType: PropTypes.oneOf(['M', 'A']).isRequired,
  hasRGB: PropTypes.bool,
  hasDSM: PropTypes.bool.isRequired,
  segments: segmentPropType.isRequired,
  roofAreasSqFt: PropTypes.array,
  roofAreasUnit: PropTypes.string,
  pitchInDegrees: PropTypes.bool,
  onPitchInDegreesChange: PropTypes.func.isRequired,
  segmentAzimuthHandler: PropTypes.func.isRequired,
  segmentPitchAndAreaHandler: PropTypes.func.isRequired,
  segmentEaveHeightHandler: PropTypes.func.isRequired,
  deleteShapeHandler: PropTypes.func.isRequired,
  addPointToPolygonHandler: PropTypes.func.isRequired,
  removePointFromPolygonHandler: PropTypes.func.isRequired,
  measureHandler: PropTypes.func.isRequired,
  runOptimizationHandler: PropTypes.func.isRequired,
  runEdgeDetectionHandler: PropTypes.func.isRequired,
  runRectificationHandler: PropTypes.func.isRequired,
  runAIHandler: PropTypes.func.isRequired,
  canSelectDifferentImage: PropTypes.bool,
  onDroneAppHandler: PropTypes.func.isRequired,
};

export default RoofSegmentsTab;
